import type { CanvasElement, Column, Content } from 'pdfmake/interfaces';
import { lineSymbolSwatchSvg } from '../../components/line.symbol.render';
import { ColorPaletteBlue } from '../../components/theme.palette';
import { T, Translate } from '../../i18n';
import {
	type LegendItem,
	legendItemLabel,
	type LegendSwatch,
	LEGEND_ITEMS,
} from '../topological.editor.map/map.legend';
import { type PoleAccuracy, poleAccuracyColor } from '../topological.editor.map/map.point/map.point';
import { HEADING_COLOR } from './map.print.theme';

const SWATCH_COL_WIDTH = 18;
const SWATCH_CENTER_Y = 5;
const POINT_RADIUS = 4;
const ROW_FONT_SIZE = 7;

// Line-style swatches render via SVG in a wider column — canvas can't draw the utility-code text labels.
const LINE_STYLE_SWATCH_COL_WIDTH = 34;
const LINE_STYLE_SWATCH_SVG_WIDTH = 46;
const LINE_STYLE_SWATCH_SVG_HEIGHT = 16;

/** Legend grouped by kind: one column for vectors (lines), one for points, one for the collection
 *  label. Each column stacks its entries, so scanning a column shows a single category at a glance. */
export function buildLegendGrid(extraItems: LegendItem[] = []): { stack: Content[] } {
	const items = visibleItems(extraItems);
	const groups = [
		items.filter((item) => item.swatch.kind === 'line'),
		items.filter((item) => item.swatch.kind === 'lineStyle'),
		items.filter((item) => item.swatch.kind === 'point'),
		items.filter((item) => item.swatch.kind === 'collectionLabel'),
	].filter((group) => group.length > 0);

	const columns: Column[] = groups.map((group) => ({
		width: '*',
		stack: group.map((item) => ({ columns: swatchAndLabel(item), margin: [0, 1, 0, 1] })),
	}));

	return { stack: [heading(), { columns, columnGap: 16 }] };
}

// The export captures a static view with no selection, so drop the "selected collection" swatch.
function visibleItems(extraItems: LegendItem[]): LegendItem[] {
	return [...LEGEND_ITEMS, ...extraItems].filter((item) => !(item.swatch.kind === 'point' && item.swatch.selected));
}

function heading(): Content {
	const text = Translate(T.TOPO_PRINT_INCLUDES_LEGEND).toUpperCase();
	return { text, bold: true, fontSize: 9, color: HEADING_COLOR, margin: [0, 0, 0, 5] };
}

function swatchAndLabel(item: LegendItem): Column[] {
	return [
		swatchColumn(item.swatch),
		{ width: '*', text: legendItemLabel(item), fontSize: ROW_FONT_SIZE, margin: [4, 0, 0, 0] },
	];
}

/** The swatch column to the left of each label. Span-line/point swatches are vector canvases; the
 *  collection label is a filled chip and line-style swatches carry text/glyphs, which `canvas` can't
 *  render — so those use a table cell and an SVG respectively. */
function swatchColumn(swatch: LegendSwatch): Column {
	if (swatch.kind === 'collectionLabel') {
		return collectionLabelChip();
	}
	if (swatch.kind === 'lineStyle') {
		return {
			width: LINE_STYLE_SWATCH_COL_WIDTH,
			svg: lineSymbolSwatchSvg(swatch.symbol, LINE_STYLE_SWATCH_SVG_WIDTH, LINE_STYLE_SWATCH_SVG_HEIGHT),
		};
	}
	return { width: SWATCH_COL_WIDTH, canvas: swatchCanvas(swatch) };
}

/** A blue chip with centered white "abc", mirroring the on-screen collection-label swatch. */
function collectionLabelChip(): Column {
	return {
		width: SWATCH_COL_WIDTH,
		table: {
			widths: ['*'],
			body: [
				[
					{
						text: 'abc',
						color: '#ffffff',
						fontSize: 6,
						alignment: 'center',
						fillColor: rgbToHex(ColorPaletteBlue.C900),
					},
				],
			],
		},
		layout: {
			defaultBorder: false,
			paddingLeft: () => 1,
			paddingRight: () => 1,
			paddingTop: () => 1,
			paddingBottom: () => 1,
		},
	};
}

function swatchCanvas(swatch: Extract<LegendSwatch, { kind: 'line' | 'point' }>): CanvasElement[] {
	switch (swatch.kind) {
		case 'line':
			return [
				{
					type: 'line',
					x1: 0,
					y1: SWATCH_CENTER_Y,
					x2: SWATCH_COL_WIDTH - 2,
					y2: SWATCH_CENTER_Y,
					lineWidth: 3,
					lineColor: rgbToHex(swatch.color),
				},
			];
		case 'point':
			return pointSwatch(swatch.accuracy, swatch.selected);
	}
}

function pointSwatch(accuracy: PoleAccuracy, selected?: boolean): CanvasElement[] {
	const cx = POINT_RADIUS + 1;
	const elements: CanvasElement[] = [];

	// White halo behind the selected marker, echoing the on-screen selected ring.
	if (selected) {
		elements.push({
			type: 'ellipse',
			x: cx,
			y: SWATCH_CENTER_Y,
			r1: POINT_RADIUS + 1.5,
			r2: POINT_RADIUS + 1.5,
			color: '#ffffff',
		});
	}

	elements.push({
		type: 'ellipse',
		x: cx,
		y: SWATCH_CENTER_Y,
		r1: POINT_RADIUS,
		r2: POINT_RADIUS,
		color: rgbToHex(poleAccuracyColor[accuracy]),
		lineColor: '#000000',
		lineWidth: 0.5,
	});

	// The "reviewed" marker carries a small black center dot on the map; mirror it here.
	if (accuracy === 'edited') {
		elements.push({ type: 'ellipse', x: cx, y: SWATCH_CENTER_Y, r1: 1, r2: 1, color: '#000000' });
	}

	return elements;
}

/** Convert an `"r,g,b"` palette value or a `rgb()/rgba()` CSS string to a `#`-prefixed hex pdfMake understands. */
function rgbToHex(value: string): string {
	if (value.startsWith('#')) {
		return value;
	}
	const parts = value.match(/\d+/g);
	if (!parts || parts.length < 3) {
		return '#000000';
	}
	return (
		'#' +
		parts
			.slice(0, 3)
			.map((n) => Number(n).toString(16).padStart(2, '0'))
			.join('')
	);
}
